import { FormFieldLabel } from '@alison/ui';
import StarOutlined from '@mui/icons-material/StarOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo, useState } from 'react';
import { useRerun, useWorkflowCatalog, useWorkflowRuns } from '../../../lib/workflows.hooks.js';
import { downloadCsv } from '../../creatives/data/csv.js';
import { MenuSearchInput } from '../../filters/MenuSearchInput.js';
import { FILTER_BAR_ROW_SX } from '../../filters/menuStyles.js';
import { SingleSelectControl } from '../../filters/SingleSelectControl.js';
import { OutcomeDrawer } from '../OutcomeDrawer.js';
import type { ReportScope } from '../reportMeta.js';
import type { MapOutcome } from '../sessionRows.js';
import { RunsTable } from './RunsTable.js';
import {
  choicesOf,
  isFiltered,
  linkRuns,
  type RunFilters,
  shownRuns,
  type WorkflowRunRow,
} from './runRows.js';
import { runsToCsv } from './runsCsv.js';

const PAGE_LIMIT = 50;
const ALL = 'all';
const STARRED_KEY = 'workflows.runs.starred';

interface OpenedOutcome {
  outcome: MapOutcome;
  scope: ReportScope;
}

export type OpenedRun = Pick<
  WorkflowRunRow,
  'runId' | 'actionType' | 'accountIds' | 'startDate' | 'endDate'
>;

export interface RunsTabProps {
  leadingKpi: string;
  onOpenRun: (run: OpenedRun) => void;
}

function namedChoices(names: string[]): Array<{ id: string; label: string }> {
  return [{ id: ALL, label: 'All' }, ...names.map((name) => ({ id: name, label: name }))];
}

// Per-viewer only: storage can be blocked, and then stars last for this visit.
function readStarred(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STARRED_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeStarred(ids: string[]): void {
  try {
    localStorage.setItem(STARRED_KEY, JSON.stringify(ids));
  } catch {
    // Blocked storage keeps the stars in memory only.
  }
}

function scopeOf(row: WorkflowRunRow): ReportScope {
  return { startDate: row.startDate, endDate: row.endDate, accountIds: row.accountIds };
}

export function RunsTab({ leadingKpi, onOpenRun }: RunsTabProps): ReactNode {
  const { runs, total, isPending, isError, retry, hasMore, isLoadingMore, loadMore } =
    useWorkflowRuns(PAGE_LIMIT);
  const catalog = useWorkflowCatalog();
  const rerun = useRerun();
  const [starred, setStarred] = useState<string[]>(readStarred);
  const [opened, setOpened] = useState<OpenedOutcome | null>(null);
  const [filters, setFilters] = useState<RunFilters>({
    search: '',
    workflow: null,
    owner: null,
    starredOnly: false,
  });

  const rows = useMemo(() => linkRuns(runs), [runs]);
  const shown = shownRuns(rows, filters, starred);
  const byRun = new Map(rows.map((row) => [row.runId, row]));
  const catalogNames = (catalog.data?.groups ?? []).flatMap((group) =>
    group.items.map((item) => item.name),
  );

  const toggleStar = (runId: string): void => {
    const next = starred.includes(runId)
      ? starred.filter((kept) => kept !== runId)
      : [...starred, runId];
    setStarred(next);
    writeStarred(next);
  };

  const startAgain = (row: WorkflowRunRow): void => {
    rerun.mutate({ runId: row.runId }, { onSuccess: ({ runId }) => onOpenRun({ ...row, runId }) });
  };

  const openOutcome = (row: WorkflowRunRow, runId: string): void => {
    const outcome = row.outcomes.find((entry) => entry.runId === runId);
    if (outcome === undefined) return;
    setOpened({
      outcome: { runId, name: outcome.title, kind: outcome.kind, date: null },
      scope: scopeOf(row),
    });
  };

  if (isError && runs.length === 0) {
    return (
      <Stack sx={{ py: 6, gap: 2, alignItems: 'center' }}>
        <Alert severity="error">Runs could not be loaded.</Alert>
        <Button variant="outlined" onClick={retry}>
          Try again
        </Button>
      </Stack>
    );
  }

  if (isPending) {
    return (
      <Stack sx={{ py: 6, alignItems: 'center' }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack sx={{ gap: 3 }}>
      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}
      >
        <Box sx={FILTER_BAR_ROW_SX}>
          <Box sx={{ display: 'flex', width: 210 }}>
            <MenuSearchInput
              placeholder="Search"
              value={filters.search}
              onChange={(search) => setFilters({ ...filters, search })}
            />
          </Box>
          <SingleSelectControl
            label="WORKFLOW"
            options={namedChoices(choicesOf(rows, (row) => row.workflow, catalogNames))}
            value={filters.workflow ?? ALL}
            onChange={(workflow) =>
              setFilters({ ...filters, workflow: workflow === ALL ? null : workflow })
            }
          />
          <SingleSelectControl
            label="OWNER"
            options={namedChoices(choicesOf(rows, (row) => row.owner))}
            value={filters.owner ?? ALL}
            onChange={(owner) => setFilters({ ...filters, owner: owner === ALL ? null : owner })}
          />
        </Box>

        <Button
          size="small"
          variant={filters.starredOnly ? 'contained' : 'outlined'}
          startIcon={<StarOutlined sx={{ fontSize: 16 }} />}
          onClick={() => setFilters({ ...filters, starredOnly: !filters.starredOnly })}
        >
          {`Starred (${starred.length})`}
        </Button>
      </Stack>

      <Stack sx={{ gap: 2 }}>
        <FormFieldLabel sx={{ color: 'text.muted' }}>
          {isFiltered(filters) ? `SHOWING ${shown.length} OF ${total}` : `ALL RUNS (${total})`}
        </FormFieldLabel>

        {isError ? (
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
            <Alert severity="error">Some runs could not be loaded.</Alert>
            <Button size="small" onClick={retry}>
              Try again
            </Button>
          </Stack>
        ) : null}

        {rerun.isError ? <Alert severity="error">The run could not be started again.</Alert> : null}

        {shown.length === 0 ? (
          <Typography sx={{ py: 6, color: 'text.muted', textAlign: 'center' }}>
            {runs.length === 0
              ? 'No runs yet. Start a workflow from the catalog and it lands here.'
              : hasMore
                ? 'None of the loaded runs match. Load more to search further.'
                : 'No runs match these filters.'}
          </Typography>
        ) : (
          <RunsTable
            rows={shown}
            starred={starred}
            rerunning={rerun.isPending ? (rerun.variables?.runId ?? null) : null}
            onToggleStar={toggleStar}
            onOpenRun={(runId) => {
              const row = byRun.get(runId);
              if (row !== undefined) onOpenRun(row);
            }}
            onOpenOutcome={openOutcome}
            onRerun={startAgain}
            onDownloadOutcomes={(row) =>
              downloadCsv(`run-${row.runId}-outcomes.csv`, runsToCsv([row]))
            }
          />
        )}
      </Stack>

      {hasMore === true ? (
        <Stack sx={{ alignItems: 'center' }}>
          <Button variant="outlined" disabled={isLoadingMore} onClick={loadMore}>
            {isLoadingMore === true ? 'Loading…' : 'Load more'}
          </Button>
        </Stack>
      ) : null}

      <OutcomeDrawer
        outcome={opened?.outcome ?? null}
        scope={opened?.scope ?? null}
        leadingKpi={leadingKpi}
        onClose={() => setOpened(null)}
      />
    </Stack>
  );
}
